import { Builder, Field, From, inc, Schema } from '@bonusly/schema';

import { ApprovalLevelSerializer } from './approval-level';
import { Condition, GroupType, NullableConditionsSerializer } from './groups';
import { User } from './users';

export enum IncentiveVisibility {
	Public = 'publicly_visible',
	Private = 'privately_visible',
	Team = 'immediate_team',
}

export enum IncentiveState {
	Active = 'active',
	Inactive = 'inactive',
}

export enum ClaimLimitPeriod {
	Week = 'week',
	Month = 'month',
	Quarter = 'quarter',
	Year = 'year',
	Lifetime = 'lifetime',
}

@Schema()
export class Incentive {
	@Field()
	@From('id')
	public id = '';

	@Field(String)
	@From('name')
	public name = '';

	@Field()
	@From('giverId')
	public giverId = '';

	@Field()
	@From('periodType')
	public periodType: ClaimLimitPeriod | null = null;

	@Field()
	@From('amount')
	public amount: number | null = null;

	@Field(String)
	@From('message')
	public message = '';

	@Field()
	@From('iconUrl')
	public iconUrl: string | null = null;

	@Field()
	@From('state')
	public state: string | null = null;

	@Field()
	@From('visibility')
	public visibility: IncentiveVisibility = IncentiveVisibility.Public;

	@Field()
	@From('requireProof')
	public requireProof: boolean | null = null;

	@Field()
	@From('conditionsOperator')
	public conditionsOperator: GroupType | null = null;

	@Field(NullableConditionsSerializer)
	@From('conditions')
	public conditions: Condition[] | null = null;

	@Field()
	@From('claimLimit')
	public claimLimit: number | null = null;

	@Field()
	@From('quantity')
	public quantity: number | null = null;

	@Field()
	@From('pendingApprovals')
	public pendingApprovals: number = 0;

	@Field()
	@From('type')
	public type = 'incentive';
}

@Schema()
export class IncentiveDetail {
	@Field()
	@From('id')
	public id = '';

	@Field()
	@From('name')
	public name = '';

	@Field()
	@From('description')
	public description = '';

	@Field()
	@From('giverId')
	public giverId = '';

	@Field()
	@From('periodType')
	public periodType: ClaimLimitPeriod = ClaimLimitPeriod.Month;

	@Field()
	@From('amount')
	public amount = 0;

	@Field()
	@From('message')
	public message = '';

	@Field()
	@From('iconUrl')
	public iconUrl: string | null = null;

	@Field()
	@From('state')
	public state: IncentiveState = IncentiveState.Active;

	@Field()
	@From('visibility')
	public visibility: IncentiveVisibility = IncentiveVisibility.Public;

	@Field()
	@From('requireProof')
	public requireProof: boolean = false;

	@Field()
	@From('conditionsOperator')
	public conditionsOperator: GroupType | null = null;

	@Field(NullableConditionsSerializer)
	@From('conditions')
	public conditions: Condition[] | null = null;

	@Field()
	@From('claimLimit')
	public claimLimit: number | null = null;

	@Field()
	@From('quantity')
	public quantity: number | null = null;

	@Field()
	@From('pendingApprovals')
	public pendingApprovals = 0;

	@Field(ApprovalLevelSerializer)
	@From('approvalLevels')
	public approvalLevels: User[][] = [];

	@Field()
	@From('type')
	public type = 'incentive';

	get isActive() {
		return this.state === IncentiveState.Active;
	}
}

export type RecentClaimTimeframe = 'day' | 'week' | 'month';

@Schema()
export class RecentClaim {
	@Field()
	@From('timeframe')
	public timeframe: RecentClaimTimeframe = 'day';

	@Field([User])
	@From('receivers')
	public receivers: User[] = [];

	@Field(Incentive)
	@From('incentive')
	public incentive: Incentive = new Incentive();
}

@Schema()
export class IncentiveMetadata {
	@Field(Incentive)
	@From('incentive')
	public incentive = new Incentive();

	@Field(User)
	@From('user')
	public user = new User();

	@Field()
	@From('claimable')
	public claimable = true;
}

// Builders
export const IncentiveBuilder = new Builder(Incentive, {
	id: inc(String),
	name: 'Test Incentive',
	amount: 100,
	message: 'Great work!',
	state: IncentiveState.Active,
	visibility: IncentiveVisibility.Public,
	requireProof: false,
	conditionsOperator: GroupType.And,
	conditions: () => [],
	claimLimit: null,
	quantity: null,
	periodType: ClaimLimitPeriod.Month,
	pendingApprovals: 0,
})
	.as('public', {
		visibility: IncentiveVisibility.Public,
	})
	.as('private', {
		visibility: IncentiveVisibility.Private,
	})
	.as('team', {
		visibility: IncentiveVisibility.Team,
	})
	.as('inactive', {
		state: IncentiveState.Inactive,
	});

export const IncentiveDetailBuilder = new Builder(IncentiveDetail, {
	id: inc(String),
	name: 'Test Incentive',
	description: 'Test description',
	amount: 100,
	message: 'Great work!',
	state: IncentiveState.Active,
	visibility: IncentiveVisibility.Public,
	requireProof: false,
	conditionsOperator: GroupType.And,
	conditions: () => [],
	claimLimit: null,
	quantity: null,
	periodType: ClaimLimitPeriod.Month,
	pendingApprovals: 0,
	approvalLevels: () => [],
})
	.as('public', {
		visibility: IncentiveVisibility.Public,
	})
	.as('private', {
		visibility: IncentiveVisibility.Private,
	})
	.as('team', {
		visibility: IncentiveVisibility.Team,
	})
	.as('inactive', {
		state: IncentiveState.Inactive,
	});
